import { useCallback } from "react"

import { useRecoilState, useRecoilValue } from "recoil"

import {
  activeFileSelector,
  activePluginSelector,
  pluginStudioStateSelector,
} from "../selectors"
import {
  CodeEditorPluginId,
  FILE_EXTENSIONS,
  getCodeEditorPluginId,
  getFileTypeFromName,
  getViewFileName,
  isCustomView,
  METADATA_FILE_NAME,
  setCodeEditorPluginId,
  UserRole,
  ViewFileName,
} from "../types"

// Debug mode toggle
const DEBUG_MODE = true

/**
 * Expected structure for plugins in the FileExplorer component
 */
interface FileExplorerPluginStructure {
  id: string
  name: string
  path: string
  folders: {
    backend: Array<{
      id: string
      name: string
      type: "file" | "folder"
      path: string
    }>
    frontend: Array<{
      id: string
      name: string
      type: "file" | "folder"
      path: string
    }>
    metadata: Array<{
      id: string
      name: string
      type: "file" | "folder"
      path: string
    }>
  }
}

/**
 * Hook that adapts the Recoil-based plugin state for use with the file explorer component.
 * This hook is for Plugin Studio editing and uses codeEditorPluginId.
 *
 * @returns Object containing file explorer state and functions
 */
export function useFileExplorer() {
  const [state, setState] = useRecoilState(pluginStudioStateSelector)
  const activePlugin = useRecoilValue(activePluginSelector)
  const activeFile = useRecoilValue(activeFileSelector)

  // Log the expected Plugin Studio structure
  console.log("[useFileExplorer] ⚠️ EXPECTING PLUGIN STUDIO INTERFACE ⚠️")
  console.log("[useFileExplorer] Required properties:", {
    id: "string",
    name: "string",
    description: "string",
    manifest: "object",
    files: "PluginFile[]",
    createdAt: "number",
  })

  /**
   * Select a plugin by id for editing in the studio
   * @param pluginId The ID of the plugin to select
   */
  const selectPlugin = useCallback(
    (pluginId: string) => {
      console.log(`[d2] selectPlugin called with pluginId: ${pluginId}`)

      if (DEBUG_MODE) {
        console.log(
          `[PLUGIN-SELECT] Current activePlugin: ${activePlugin?.id}, new pluginId: ${pluginId}`,
        )
      }

      if (pluginId === "new") {
        // For "new" plugin, we'll need to call a method from usePluginStudio
        // But we'll have the component handle that
        console.log(`[d2] "new" plugin selected, letting component handle it`)
        return
      }

      setState((currentState) => {
        console.log(`[d2] setState in selectPlugin for pluginId: ${pluginId}`)

        const plugin = currentState.plugins.find((p) => p.id === pluginId)

        // If we don't find the plugin, just update the active plugin ID
        if (!plugin) {
          console.log(`[d2] Plugin not found, only updating codeEditorPluginId`)
          // Use the setter function to update only the code editor plugin ID
          return setCodeEditorPluginId(currentState, pluginId)
        }

        // Log the plugin to verify its structure
        console.log(`[d2] Found plugin in selectPlugin:`, {
          id: plugin.id,
          name: plugin.name,
          hasFiles: Array.isArray(plugin.files),
          filesCount: plugin.files?.length || 0,
        })

        // Find the guest file to auto-select
        const guestFile = plugin.files.find((f) => f.type === UserRole.GUEST)
        console.log(
          `[d2] Guest file in selectPlugin:`,
          guestFile
            ? {
                id: guestFile.id,
                name: guestFile.name,
                type: guestFile.type,
              }
            : "Not found",
        )

        if (DEBUG_MODE && guestFile) {
          console.log(
            `[PLUGIN-SELECT-FILE] Found guest file with ID: ${guestFile.id}`,
          )
          console.log(
            `[PLUGIN-SELECT-FILE] Content hash: ${guestFile.content ? guestFile.content.substring(0, 20).replace(/\s/g, "") : "no content"}`,
          )
        }

        // Update plugin states to select this plugin and its guest file
        const updatedPluginStates = {
          ...currentState.pluginStates,
        }

        // If this plugin doesn't have a state entry yet, create one
        if (!updatedPluginStates[pluginId]) {
          console.log(`[d2] Creating new plugin state for ${pluginId}`)
          updatedPluginStates[pluginId] = {
            activeFileId: guestFile?.id || null,
            openFileIds: guestFile?.id ? [guestFile.id] : [],
            activeViewType: UserRole.GUEST,
          }
        }
        // If it has a state but no active file, select the guest file
        else if (!updatedPluginStates[pluginId].activeFileId && guestFile) {
          console.log(`[d2] Updating existing plugin state with guest file`)
          updatedPluginStates[pluginId] = {
            ...updatedPluginStates[pluginId],
            activeFileId: guestFile.id,
            openFileIds: updatedPluginStates[pluginId].openFileIds.includes(
              guestFile.id,
            )
              ? updatedPluginStates[pluginId].openFileIds
              : [...updatedPluginStates[pluginId].openFileIds, guestFile.id],
            activeViewType: UserRole.GUEST,
          }
        }

        // Use the setter function to update the state with the new code editor plugin ID
        const updatedState = setCodeEditorPluginId(currentState, pluginId)

        console.log(
          `[d2] Final state in selectPlugin: pluginState =`,
          updatedPluginStates[pluginId],
        )

        return {
          ...updatedState,
          pluginStates: updatedPluginStates,
        }
      })
    },
    [setState, activePlugin],
  )

  /**
   * Select a file by path, plugin id and name
   * @param path File path
   * @param pluginId Plugin ID
   * @param fileName File name
   */
  const selectFile = useCallback(
    (path: string, pluginId: string, fileName: string) => {
      console.log(`[d2] selectFile called with:`, {
        path,
        pluginId,
        fileName,
      })

      if (DEBUG_MODE) {
        console.log(
          `[FILE-SELECT-START] Current activeFile: ${activeFile?.id}, selecting in plugin: ${pluginId}`,
        )
      }

      // Find the plugin
      const plugin = state.plugins.find((p) => p.id === pluginId)
      if (!plugin) {
        console.error(`[d2] Plugin not found with ID: ${pluginId}`)
        return
      }

      console.log(`[d2] Found plugin in selectFile:`, {
        id: plugin.id,
        name: plugin.name,
        filesCount: plugin.files.length,
        activePluginId: getCodeEditorPluginId(state),
      })

      // Determine file type using the helper function
      const fileType = getFileTypeFromName(fileName)

      console.log(`[d2] Determined file type: ${fileType}`)

      // Find the file by type
      const file = plugin.files.find((f) => {
        if (fileType === "metadata") {
          return (
            f.path.includes("manifest.json") || f.name.includes("manifest.json")
          )
        }
        if (fileType === UserRole.CUSTOM) {
          return f.name === fileName
        }
        return f.type === fileType
      })

      if (!file) {
        console.error(`[d2] File not found with type: ${fileType}`)
        return
      }

      console.log(`[d2] Found file:`, {
        id: file.id,
        type: file.type,
        path: file.path,
        name: file.name,
      })

      // Add detailed content logging
      console.log(
        `[d2-file-content] File content length: ${file.content?.length || 0}`,
      )
      if (file.content) {
        console.log(
          `[d2-file-content] First 50 chars: ${file.content.substring(0, 50)}...`,
        )
      } else {
        console.log(`[d2-file-content] No content found in file`)
      }

      if (DEBUG_MODE) {
        console.log(
          `[FILE-SELECT] Selected file content snapshot: ${file.content ? file.content.substring(0, 30) + "..." : "empty"}`,
        )
        console.log(
          `[FILE-SELECT] File ID: ${file.id}, Plugin ID: ${pluginId}, matching: ${file.id.includes(pluginId)}`,
        )

        // Verify all file references before state update
        const prevFileContent = activeFile?.content?.substring(0, 20) || "none"
        console.log(
          `[FILE-SWITCH] Before switch - activeFile: ${activeFile?.id}, content: ${prevFileContent}`,
        )
        console.log(
          `[FILE-SWITCH] New file: ${file.id}, content: ${file.content?.substring(0, 20) || "none"}`,
        )
      }

      // Update the state with selected file
      setState((currentState) => {
        console.log(
          `[d2] Updating state with selected file ID: ${file.id} and plugin ID: ${pluginId}`,
        )

        // First, ensure codeEditorPluginId is set to the current plugin
        console.log(
          `[d2] Current codeEditorPluginId: ${getCodeEditorPluginId(currentState)}`,
        )
        console.log(`[d2] Setting codeEditorPluginId to: ${pluginId}`)

        const pluginState = currentState.pluginStates[pluginId] || {
          activeFileId: null,
          openFileIds: [],
          activeViewType: UserRole.GUEST,
        }

        console.log(`[d2] Current plugin state for ${pluginId}:`, pluginState)

        // Add to open files if not already open
        const openFileIds = pluginState.openFileIds.includes(file.id)
          ? pluginState.openFileIds
          : [...pluginState.openFileIds, file.id]

        console.log(`[d2] Updated openFileIds:`, openFileIds)

        const isEditorViewType = (type: string | UserRole): type is UserRole =>
          type === UserRole.GUEST ||
          type === UserRole.HOST_EVENT_VIEW ||
          type === UserRole.HOST_CONFIG_VIEW

        // Use the setter function to update the code editor plugin ID
        const updatedState = setCodeEditorPluginId(currentState, pluginId)

        const updatedPluginState = {
          ...pluginState,
          activeFileId: file.id,
          openFileIds,
          activeViewType: isEditorViewType(fileType)
            ? fileType
            : pluginState.activeViewType,
        }

        console.log(
          `[d2] Final plugin state for ${pluginId}:`,
          updatedPluginState,
        )

        if (DEBUG_MODE) {
          // Verify the plugin and file references AFTER creating the updated state
          // but BEFORE returning it
          const updatedFileRef = currentState.plugins
            .find((p) => p.id === pluginId)
            ?.files.find((f) => f.id === file.id)

          if (updatedFileRef) {
            console.log(
              `[STATE-UPDATE-VERIFY] File reference check - ID: ${updatedFileRef.id}`,
            )
            console.log(
              `[STATE-UPDATE-VERIFY] Content hash: ${updatedFileRef.content?.substring(0, 20).replace(/\s/g, "") || "missing"}`,
            )
          } else {
            console.log(
              `[STATE-UPDATE-VERIFY] Could not find file reference for verification!`,
            )
          }
        }

        return {
          ...updatedState,
          pluginStates: {
            ...currentState.pluginStates,
            [pluginId]: updatedPluginState,
          },
          isPreviewStale: true,
        }
      })

      if (DEBUG_MODE) {
        // After state update, verify the plugin and file are correctly set
        setTimeout(() => {
          const currentActiveFile = activeFile
          console.log(
            `[POST-SELECT-VERIFY] Active file after state update: ${currentActiveFile?.id}`,
          )
          if (currentActiveFile) {
            console.log(
              `[POST-SELECT-VERIFY] Content hash: ${currentActiveFile.content?.substring(0, 20).replace(/\s/g, "") || "missing"}`,
            )
          }
        }, 0)
      }
    },
    [state.plugins, setState, activeFile],
  )

  /**
   * Convert plugin files to the format expected by the file explorer
   * @returns Array of plugins adapted for the file explorer component
   */
  const adaptPluginsForExplorer = useCallback(() => {
    console.log(
      "[useFileExplorer] adaptPluginsForExplorer called with",
      state.plugins.length,
      "plugins",
    )

    if (state.plugins.length > 0) {
      const firstPlugin = state.plugins[0]
      console.log("[useFileExplorer] First plugin details:", {
        id: firstPlugin.id,
        name: firstPlugin.name,
        hasFiles: Array.isArray(firstPlugin.files),
        filesCount: firstPlugin.files?.length || 0,
        fileTypes: firstPlugin.files?.map((f) => f.type) || [],
      })

      if (DEBUG_MODE) {
        // Log contents of first file from first plugin for debugging
        const firstFile = firstPlugin.files && firstPlugin.files[0]
        if (firstFile) {
          console.log(
            `[EXPLORER-FILES] First file: ${firstFile.id}, type: ${firstFile.type}`,
          )
          console.log(
            `[EXPLORER-FILES] Content hash: ${firstFile.content?.substring(0, 20).replace(/\s/g, "") || "no content"}`,
          )
        }
      }
    }

    // Added detailed logging for each plugin to identify any issues
    return state.plugins.map((plugin, index) => {
      console.log(`[useFileExplorer] Processing plugin at index ${index}:`, {
        id: plugin.id,
        name: plugin.name || plugin.manifest?.name,
        hasFiles: Array.isArray(plugin.files),
        filesCount: plugin.files?.length || 0,
      })

      // Create the structure expected by FileExplorer
      const explorerStructure: FileExplorerPluginStructure = {
        id: plugin.id,
        name: plugin.name || plugin.manifest?.name || "Untitled Plugin",
        path: `/${plugin.id}`,
        folders: {
          backend: [],
          frontend: [
            {
              id: `${plugin.id}-guest-view`,
              name: ViewFileName.GUEST,
              type: "file",
              path: `/${plugin.id}/frontend/${ViewFileName.GUEST}`,
            },
            {
              id: `${plugin.id}-host-view`,
              name: ViewFileName.HOST_EVENT_VIEW,
              type: "file",
              path: `/${plugin.id}/frontend/${ViewFileName.HOST_EVENT_VIEW}`,
            },
            {
              id: `${plugin.id}-hostConfigView`,
              name: ViewFileName.HOST_CONFIG_VIEW,
              type: "file",
              path: `/${plugin.id}/frontend/${ViewFileName.HOST_CONFIG_VIEW}`,
            },
            // Add custom views dynamically
            ...plugin.files
              .filter((file) => isCustomView(file))
              .map((file) => {
                console.log(
                  `[useFileExplorer] Adding custom view: ${file.name}`,
                )
                return {
                  id: `${plugin.id}-${file.id}`,
                  name: file.name,
                  type: "file" as const,
                  path: `/${plugin.id}/frontend/${file.name}`,
                }
              }),
          ],
          metadata: [
            {
              id: `${plugin.id}-manifest`,
              name: METADATA_FILE_NAME,
              type: "file",
              path: `/${plugin.id}/metadata/${METADATA_FILE_NAME}`,
            },
          ],
        },
      }

      // Log before attempting to access files
      console.log(
        `[useFileExplorer] About to check plugin.files for plugin ${plugin.id}:`,
        {
          hasFiles: Array.isArray(plugin.files),
          filesLength: plugin.files?.length || 0,
        },
      )

      // Check if files exist and add proper safety check
      if (!plugin.files || !Array.isArray(plugin.files)) {
        console.error(
          `[useFileExplorer] Plugin ${plugin.id} has no files array!`,
        )
        console.error(`[useFileExplorer] ⚠️ PLUGIN TYPE MISMATCH ⚠️`)
        console.error(
          `[useFileExplorer] This is likely because the plugin came from pluginConverters.ts`,
        )
        console.error(
          `[useFileExplorer] which returns a Plugin from recoil/plugins.ts without files property`,
        )

        // Inspect the actual plugin object
        console.error(
          `[useFileExplorer] Plugin object keys:`,
          Object.keys(plugin),
        )
        console.error(`[useFileExplorer] Plugin manifest:`, plugin.manifest)
        console.error(`[useFileExplorer] Plugin code:`, plugin.code)

        // Return without trying to access plugin.files
        return {
          ...explorerStructure,
          manifest: plugin.manifest,
          code: {
            frontendGuestCodeWeb: "",
            frontendHostViewCodeWeb: "",
            frontendHostConfigCodeWeb: "",
          },
        }
      }

      // Now files exist, log them
      console.log(`[useFileExplorer] Plugin ${plugin.id} files:`, {
        fileIds: plugin.files.map((f) => f.id),
        fileTypes: plugin.files.map((f) => f.type),
      })

      if (DEBUG_MODE) {
        // Create a map of file content hashes for this plugin to help diagnose issues
        const fileContentMap = plugin.files.map((f) => ({
          id: f.id,
          type: f.type,
          contentHash: f.content
            ? f.content.substring(0, 20).replace(/\s/g, "")
            : "empty",
        }))
        console.log(
          `[PLUGIN-FILES] Content map for plugin ${plugin.id}:`,
          fileContentMap,
        )
      }

      // Also include the original properties needed by some components
      return {
        ...explorerStructure,
        manifest: plugin.manifest,
        code: {
          frontendGuestCodeWeb:
            plugin.files.find((f) => f.type === UserRole.GUEST)?.content || "",
          frontendHostViewCodeWeb:
            plugin.files.find((f) => f.type === UserRole.HOST_EVENT_VIEW)
              ?.content || "",
          frontendHostConfigCodeWeb:
            plugin.files.find((f) => f.type === UserRole.HOST_CONFIG_VIEW)
              ?.content || "",
        },
      }
    })
  }, [state.plugins])

  /**
   * Find active file path for file explorer
   * @returns Path string for the active file
   */
  const getActiveFilePath = useCallback(() => {
    if (!activePlugin || !activeFile) return ""

    // Create a path based on plugin ID and file type
    // check first for custom view as type can be guest for custom view
    const fileName = isCustomView(activeFile)
      ? activeFile.name
      : activeFile.type === UserRole.GUEST
        ? ViewFileName.GUEST
        : activeFile.type === UserRole.HOST_EVENT_VIEW
          ? ViewFileName.HOST_EVENT_VIEW
          : activeFile.type === UserRole.HOST_CONFIG_VIEW
            ? ViewFileName.HOST_CONFIG_VIEW
            : activeFile.name

    return `/${activePlugin.id}/frontend/${fileName}`
  }, [activePlugin, activeFile])

  return {
    plugins: adaptPluginsForExplorer(),
    activePlugin,
    activePluginId: getCodeEditorPluginId(state), // Use the accessor function
    activeFilePath: getActiveFilePath(),
    selectPlugin,
    selectFile,
  }
}
